import { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import API from 'api/api';
import { translateApiError } from 'i18n/translateApiError';
import { normalizeRolePreference } from 'domains/auth/utils/registrationRole';

export function useCreateUserWithRole() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const createUserWithRole = async (
    uid,
    email,
    displayName,
    rolePreference,
    idTokenOverride = null
  ) => {
    setLoading(true);
    setError(null);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const auth = getAuth();
      const normalizedRolePreference = normalizeRolePreference(rolePreference, null);
      if (!normalizedRolePreference) {
        throw new Error('Invalid role preference');
      }
      const token = idTokenOverride || (await auth.currentUser?.getIdToken());
      if (!token) {
        throw new Error('Authentication token is missing');
      }

      const res = await API.post(
        '/users',
        { uid, email, displayName, rolePreference: normalizedRolePreference },
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        }
      );

      if (mountedRef.current) {
        setLoading(false);
        return res?.data;
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        if (mountedRef.current) setLoading(false);
        return;
      }

      console.error('Failed to create user with role:', err);
      const errorMessage = translateApiError(err, 'Failed to create user with role');

      if (mountedRef.current) {
        setError(errorMessage);
        setLoading(false);
      }

      // Re-throw the error so the caller knows the operation failed
      throw new Error(errorMessage);
    }
  };

  return { createUserWithRole, loading, error };
}
